import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  Paper,
  Text,
  Group,
  Stack,
  Avatar,
  Badge,
  Table,
  LoadingOverlay,
  ActionIcon,
  Tooltip,
  CopyButton,
  Button,
  Popover,
} from "@mantine/core";
import {
  IconMedal,
  IconArrowLeft,
  IconMail,
  IconCheck,
  IconCopy,
  IconPhone,
  IconCake,
  IconMapPin,
  IconAddressBook,
  IconClock,
} from "@tabler/icons-react";
import { API_BASE_URL } from "../../config/baseUrls";

export default function MegaQuizLeaderboard() {
  const { quizId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const quizTitle = location.state?.quizTitle || "Mega Quiz";

  const [leaderboardData, setLeaderboardData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchLeaderboard = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem("adminToken");
        const response = await fetch(
          `${API_BASE_URL}/megaquiz/MegaQuizRanking/${quizId}?page=1`,
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        );
        const result = await response.json();

        if (result.rankings) {
          setLeaderboardData(result.rankings);
        }
      } catch (err) {
        console.error("Failed to fetch leaderboard", err);
      } finally {
        setLoading(false);
      }
    };

    fetchLeaderboard();
  }, [quizId]);

  // --- NEW: THE CONTACT INFO POPOVER ---
  const ContactPopover = ({ player }) => (
    <Popover width={320} position="bottom" withArrow shadow="lg" radius="md">
      <Popover.Target>
        <Button
          variant="light"
          color="blue"
          size="xs"
          leftSection={<IconAddressBook size={14} />}
        >
          Contact Details
        </Button>
      </Popover.Target>
      <Popover.Dropdown p="md">
        <Stack gap="sm">
          <Text size="sm" fw={700} c="dimmed" tt="uppercase" lts={1}>
            Player Information
          </Text>

          {/* Email Row */}
          <Group justify="space-between" wrap="nowrap">
            <Group gap="sm" wrap="nowrap">
              <IconMail size={16} color="#adb5bd" />
              <Text size="sm" truncate w={180}>
                {player.email}
              </Text>
            </Group>
            <CopyButton value={player.email} timeout={2000}>
              {({ copied, copy }) => (
                <Tooltip
                  label={copied ? "Copied!" : "Copy Email"}
                  position="top"
                >
                  <ActionIcon
                    size="sm"
                    color={copied ? "teal" : "gray"}
                    variant="subtle"
                    onClick={copy}
                  >
                    {copied ? <IconCheck size={14} /> : <IconCopy size={14} />}
                  </ActionIcon>
                </Tooltip>
              )}
            </CopyButton>
          </Group>

          {/* Phone Row */}
          <Group justify="space-between" wrap="nowrap">
            <Group gap="sm" wrap="nowrap">
              <IconPhone size={16} color="#adb5bd" />
              <Text size="sm" truncate w={180}>
                {player.phone}
              </Text>
            </Group>
            <CopyButton value={player.phone} timeout={2000}>
              {({ copied, copy }) => (
                <Tooltip
                  label={copied ? "Copied!" : "Copy Phone"}
                  position="top"
                >
                  <ActionIcon
                    size="sm"
                    color={copied ? "teal" : "gray"}
                    variant="subtle"
                    onClick={copy}
                  >
                    {copied ? <IconCheck size={14} /> : <IconCopy size={14} />}
                  </ActionIcon>
                </Tooltip>
              )}
            </CopyButton>
          </Group>

          {/* DOB Row */}
          <Group justify="space-between" wrap="nowrap">
            <Group gap="sm" wrap="nowrap">
              <IconCake size={16} color="#adb5bd" />
              <Text size="sm" truncate w={180}>
                {player.dob}
              </Text>
            </Group>
            <CopyButton value={player.dob} timeout={2000}>
              {({ copied, copy }) => (
                <Tooltip label={copied ? "Copied!" : "Copy DOB"} position="top">
                  <ActionIcon
                    size="sm"
                    color={copied ? "teal" : "gray"}
                    variant="subtle"
                    onClick={copy}
                  >
                    {copied ? <IconCheck size={14} /> : <IconCopy size={14} />}
                  </ActionIcon>
                </Tooltip>
              )}
            </CopyButton>
          </Group>

          {/* Address Row */}
          <Group justify="space-between" wrap="nowrap" align="flex-start">
            <Group gap="sm" wrap="nowrap" align="flex-start">
              <IconMapPin size={16} color="#adb5bd" style={{ marginTop: 2 }} />
              <Text size="sm" lineClamp={2} w={180}>
                {player.address}
              </Text>
            </Group>
            <CopyButton value={player.address} timeout={2000}>
              {({ copied, copy }) => (
                <Tooltip
                  label={copied ? "Copied!" : "Copy Address"}
                  position="top"
                >
                  <ActionIcon
                    size="sm"
                    color={copied ? "teal" : "gray"}
                    variant="subtle"
                    onClick={copy}
                  >
                    {copied ? <IconCheck size={14} /> : <IconCopy size={14} />}
                  </ActionIcon>
                </Tooltip>
              )}
            </CopyButton>
          </Group>
        </Stack>
      </Popover.Dropdown>
    </Popover>
  );

  return (
    <Box maxW={1200} mx="auto" pb={50}>
      {/* HEADER */}
      <Group mb="xl">
        <ActionIcon variant="light" size="lg" onClick={() => navigate(-1)}>
          <IconArrowLeft size={20} />
        </ActionIcon>
        <Box>
          <Text size="xl" fw={800} className="ota-gradient-text">
            {quizTitle} - Top 10 Winners
          </Text>
          <Text size="sm" c="dimmed">
            Review top scorers and access contact information for prize
            distribution.
          </Text>
        </Box>
      </Group>

      <Paper withBorder radius="md" p="xl" pos="relative" minH={400}>
        <LoadingOverlay visible={loading} overlayProps={{ blur: 2 }} />

        {!loading && leaderboardData.length === 0 ? (
          <Stack align="center" justify="center" h={300}>
            <IconMedal size={48} color="var(--mantine-color-gray-3)" />
            <Text c="dimmed" size="lg" fw={600}>
              No players have completed this quiz yet.
            </Text>
          </Stack>
        ) : (
          <Stack gap="xl">
            {/* OLYMPIC PODIUM FOR TOP 3 */}
            <Group justify="center" align="flex-end" gap={60} mt="xl" mb="xl">
              {/* 2nd Place (Silver) */}
              {leaderboardData[1] && (
                <Stack align="center" gap={4} mt={30}>
                  <IconMedal size={32} color="#94A3B8" />
                  <Avatar
                    src={leaderboardData[1].characterImg}
                    size={80}
                    radius="100%"
                    style={{ border: "4px solid #94A3B8" }}
                  />
                  <Text fw={800} size="md" mt="sm">
                    {leaderboardData[1].name}
                  </Text>

                  {/* UPDATED: Points & Time */}
                  <Group gap="xs" mb="sm">
                    <Badge color="gray" variant="light" size="lg">
                      {leaderboardData[1].points} pts
                    </Badge>
                    <Badge
                      color="gray"
                      variant="default"
                      size="lg"
                      leftSection={<IconClock size={12} />}
                    >
                      {leaderboardData[1].timeTaken}s
                    </Badge>
                  </Group>

                  <ContactPopover player={leaderboardData[1]} />
                </Stack>
              )}

              {/* 1st Place (Gold) */}
              {leaderboardData[0] && (
                <Stack align="center" gap={4}>
                  <IconMedal size={50} color="#FBBF24" />
                  <Avatar
                    src={leaderboardData[0].characterImg}
                    size={110}
                    radius="100%"
                    style={{
                      border: "5px solid #FBBF24",
                      boxShadow: "0 4px 14px rgba(251, 191, 36, 0.4)",
                    }}
                  />
                  <Text fw={900} size="xl" c="yellow.6" mt="sm">
                    {leaderboardData[0].name}
                  </Text>

                  {/* UPDATED: Points & Time */}
                  <Group gap="xs" mb="sm">
                    <Badge color="yellow" variant="filled" size="xl">
                      {leaderboardData[0].points} pts
                    </Badge>
                    <Badge
                      color="gray"
                      variant="default"
                      size="xl"
                      leftSection={<IconClock size={14} />}
                    >
                      {leaderboardData[0].timeTaken}s
                    </Badge>
                  </Group>

                  <ContactPopover player={leaderboardData[0]} />
                </Stack>
              )}

              {/* 3rd Place (Bronze) */}
              {leaderboardData[2] && (
                <Stack align="center" gap={4} mt={40}>
                  <IconMedal size={28} color="#B45309" />
                  <Avatar
                    src={leaderboardData[2].characterImg}
                    size={70}
                    radius="100%"
                    style={{ border: "4px solid #B45309" }}
                  />
                  <Text fw={800} size="sm" mt="sm">
                    {leaderboardData[2].name}
                  </Text>

                  {/* UPDATED: Points & Time */}
                  <Group gap="xs" mb="sm">
                    <Badge color="orange" variant="light" size="lg">
                      {leaderboardData[2].points} pts
                    </Badge>
                    <Badge
                      color="gray"
                      variant="default"
                      size="lg"
                      leftSection={<IconClock size={12} />}
                    >
                      {leaderboardData[2].timeTaken}s
                    </Badge>
                  </Group>

                  <ContactPopover player={leaderboardData[2]} />
                </Stack>
              )}
            </Group>

            {/* TABLE FOR RANKS 4-10 */}
            {leaderboardData.length > 3 && (
              <Box mt="xl">
                <Text fw={700} mb="md" size="lg">
                  Runners Up (Ranks 4-10)
                </Text>
                <Table striped highlightOnHover verticalSpacing="md">
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Rank</Table.Th>
                      <Table.Th>Player</Table.Th>
                      <Table.Th>Score</Table.Th>
                      <Table.Th>Time Taken</Table.Th>
                      <Table.Th style={{ textAlign: "right" }}>
                        Actions
                      </Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {leaderboardData.slice(3).map((player) => (
                      <Table.Tr key={player.userId}>
                        <Table.Td>
                          <Text fw={800} c="dimmed">
                            #{player.rank}
                          </Text>
                        </Table.Td>
                        <Table.Td>
                          <Group gap="sm">
                            <Avatar
                              src={player.characterImg}
                              size={40}
                              radius="xl"
                            />
                            <Text size="sm" fw={600}>
                              {player.name}
                            </Text>
                          </Group>
                        </Table.Td>
                        <Table.Td>
                          <Text fw={700}>{player.points}</Text>
                        </Table.Td>
                        <Table.Td>
                          <Text size="sm" c="dimmed">
                            {player.timeTaken}s
                          </Text>
                        </Table.Td>
                        <Table.Td style={{ textAlign: "right" }}>
                          <Group justify="flex-end">
                            <ContactPopover player={player} />
                          </Group>
                        </Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              </Box>
            )}
          </Stack>
        )}
      </Paper>
    </Box>
  );
}
